import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams } from 'react-router-dom';
import {
    BsBroadcast, BsBriefcase, BsLightningCharge, BsPlusCircle,
    BsGeoAlt, BsClock
} from 'react-icons/bs';
import { useTranslation } from 'react-i18next';
import dispatchService, { DispatchTask } from '../services/dispatchService';
import TaskPostingForm from '../components/dispatch/TaskPostingForm';
import TaskCard from '../components/dispatch/TaskCard';
import TaskDetailModal from '../components/dispatch/TaskDetailModal';
import DispatchTour from '../components/onboarding/DispatchTour';
import AppHeader from '../components/layout/AppHeader';
import { socketClient } from '../services/api';

type TabType = 'post' | 'my-tasks' | 'matches' | 'open-tasks' | 'community-tasks';

const Dispatch: React.FC = () => {
    const { t } = useTranslation();
    const { taskId } = useParams();
    const [activeTab, setActiveTab] = useState<TabType>('open-tasks');
    const [myTasks, setMyTasks] = useState<DispatchTask[]>([]);
    const [matches, setMatches] = useState<DispatchTask[]>([]);
    const [openTasks, setOpenTasks] = useState<DispatchTask[]>([]);
    const [selectedTask, setSelectedTask] = useState<DispatchTask | null>(null);
    const [autoTaskId, setAutoTaskId] = useState<string | null>(taskId || null);
    const [loading, setLoading] = useState(false);
    const [showPostForm, setShowPostForm] = useState(false);
    const [modalView, setModalView] = useState<'details' | 'apply' | 'applications'>('details');
    const [radius, setRadius] = useState(50);
    const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
    const [useLocationFilter, setUseLocationFilter] = useState(false);
    const [reportingTaskId, setReportingTaskId] = useState<string | null>(null);
    const [reportReason, setReportReason] = useState('scam');
    const [reportDescription, setReportDescription] = useState('');
    const [reportLoading, setReportLoading] = useState(false);
    const [notification, setNotification] = useState<{ type: 'success' | 'error', message: string } | null>(null);

    // Quick Filters
    const [quickFilter, setQuickFilter] = useState<'all' | 'remote' | 'on-site' | 'urgent' | 'no-cv'>('all');

    useEffect(() => {
        if (taskId) {
            setAutoTaskId(taskId);
        }
    }, [taskId]);

    useEffect(() => {
        // Subscribe to real-time matches and updates
        const unsubscribeMatch = socketClient.subscribe('task_match', () => {
            loadMatches();
            // Optional: loadOpenTasks() if matches affect general pool
        });

        const unsubscribeUpdate = socketClient.subscribe('application_update', () => {
            if (activeTab === 'my-tasks') loadMyTasks();
            if (activeTab === 'matches') loadMatches();
        });

        return () => {
            unsubscribeMatch();
            unsubscribeUpdate();
        };
    }, [activeTab]);

    // --- Browser History / Back Button Intercept ---
    useEffect(() => {
        const handlePopState = () => {
            if (showPostForm) {
                setShowPostForm(false);
            } else if (selectedTask !== null) {
                setSelectedTask(null);
                setAutoTaskId(null);
            }
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [showPostForm, selectedTask]);

    useEffect(() => {
        if (activeTab === 'my-tasks') {
            loadMyTasks();
        } else if (activeTab === 'matches') {
            loadMatches();
        } else if (activeTab === 'open-tasks' || activeTab === 'community-tasks') {
            loadOpenTasks();
        }
    }, [activeTab, useLocationFilter, radius, userLocation]); // Reload when filters change

    const getUserLocation = () => {
        if (!navigator.geolocation) {
            alert('Geolocation is not supported by your browser');
            return;
        }

        if (useLocationFilter) {
            // Toggle off
            setUseLocationFilter(false);
            setUserLocation(null);
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                setUserLocation({
                    lat: position.coords.latitude,
                    lng: position.coords.longitude
                });
                setUseLocationFilter(true);
            },
            (error) => {
                console.error('Error getting location:', error);
                alert('Unable to retrieve your location');
            }
        );
    };

    const handleReportSubmit = async () => {
        if (!reportingTaskId || !reportDescription) return;
        setReportLoading(true);
        try {
            await dispatchService.reportTask(reportingTaskId, reportReason, reportDescription);
            setNotification({ type: 'success', message: 'Report submitted successfully. Thank you for keeping TechUnity safe.' });
            setReportingTaskId(null);
            setReportDescription('');
        } catch (error) {
            console.error('Report failed:', error);
            setNotification({ type: 'error', message: 'Failed to submit report. Please try again.' });
        } finally {
            setReportLoading(false);
        }
    };

    const loadOpenTasks = async () => {
        setLoading(true);
        try {
            // Pass location params if enabled
            const response = await dispatchService.getOpenTasks(
                useLocationFilter && userLocation ? userLocation.lat : undefined,
                useLocationFilter && userLocation ? userLocation.lng : undefined,
                useLocationFilter ? radius : undefined
            );
            const tasks = Array.isArray(response) ? response : (response.tasks || (response as any).data || []);
            setOpenTasks(tasks);
        } catch (error: any) {
            console.error('Failed to load open tasks:', error);
            if (error.response) {
                console.error('Error details:', error.response.data);
            }
        } finally {
            setLoading(false);
        }
    };

    const loadMyTasks = async () => {
        setLoading(true);
        try {
            const response = await dispatchService.getUserTasks();
            const tasks = Array.isArray(response) ? response : (response.tasks || (response as any).data || []);
            setMyTasks(tasks);
        } catch (error: any) {
            console.error('Failed to load tasks:', error);
            if (error.response) {
                console.error('Error details:', error.response.data);
            }
        } finally {
            setLoading(false);
        }
    };

    const loadMatches = async () => {
        setLoading(true);
        try {
            const response = await dispatchService.getUserMatches();
            const matches = Array.isArray(response) ? response : (response.tasks || (response as any).data || []);
            setMatches(matches);
        } catch (error: any) {
            console.error('Failed to load matches:', error);
            if (error.response) {
                console.error('Error details:', error.response.data);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleTaskPosted = () => {
        setShowPostForm(false);
        setSelectedTask(null); // Clear selected task after edit/post
        setActiveTab('my-tasks');
        loadMyTasks();
    };

    const handleEditTask = (task: DispatchTask) => {
        setSelectedTask(task);
        setShowPostForm(true);
        window.history.pushState({ dispatchLevel: 'edit' }, '', window.location.hash);
    };

    const handleApplyClick = (taskId: string) => {
        // Find the task
        const task = openTasks.find(t => t._id === taskId) || matches.find(t => t._id === taskId);
        if (task) {
            setSelectedTask(task);
            setModalView('apply');
        } else {
            // Fallback for direct link or if not in current tab
            setAutoTaskId(taskId);
            setModalView('apply');
        }
        window.history.pushState({ dispatchLevel: 'apply' }, '', window.location.hash);
    };

    const handleAccept = async (taskId: string) => {
        try {
            await dispatchService.acceptTask(taskId);
            // Refresh logic based on active tab
            if (activeTab === 'open-tasks') loadOpenTasks();
            if (activeTab === 'matches') loadMatches();

            // Show success message
            alert('Task accepted! Project workspace created.');
        } catch (error: any) {
            alert(error.response?.data?.error || 'Failed to accept task');
        }
    };

    const handleDecline = async (taskId: string) => {
        try {
            await dispatchService.declineTask(taskId);
            // Refresh matches
            loadMatches();
        } catch (error) {
            console.error('Failed to decline task:', error);
        }
    };

    const handleDelete = async (taskId: string) => {
        if (!window.confirm('Are you sure you want to delete this task?')) return;
        try {
            await dispatchService.deleteTask(taskId);
            loadMyTasks();
            if (autoTaskId === taskId) setAutoTaskId(null);
            if (selectedTask?._id === taskId) setSelectedTask(null);
        } catch (error) {
            console.error('Failed to delete task:', error);
            alert('Failed to delete task');
        }
    };

    const handleStatusChange = async (taskId: string, status: string) => {
        if (!window.confirm(`Are you sure you want to mark this task as ${status === 'expired' ? 'taken/closed' : status}?`)) return;
        try {
            await dispatchService.updateTaskStatus(taskId, status);
            loadMyTasks();
        } catch (error) {
            console.error('Failed to update task status:', error);
            alert('Failed to update task status');
        }
    };

    const handleTaskClick = (task: DispatchTask) => {
        setSelectedTask(task);
        setModalView('details');
        window.history.pushState({ dispatchLevel: 'details' }, '', window.location.hash);
    };

    const handleCloseModal = () => {
        setSelectedTask(null);
        setAutoTaskId(null);
        setModalView('details');
        if (window.history.state?.dispatchLevel) {
            window.history.back();
        }
    };

    return (
        <div className="min-h-screen bg-premium-wood-dark relative">
            <DispatchTour />
            {/* Background effects */}
            <div
                className="absolute inset-0 z-0 opacity-20 pointer-events-none"
                style={{
                    backgroundImage: `url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400"><filter id="noise"><feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="4" /></filter><rect width="400" height="400" filter="url(%23noise)" opacity="0.3"/></svg>')`
                }}
            />
            <div className="absolute inset-0 z-0 bg-gradient-to-b from-black/60 via-premium-wood-dark/80 to-black/90 pointer-events-none" />

            {/* Header */}
            <AppHeader />

            <div className="relative z-10 container mx-auto px-4 py-8 max-w-7xl">
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-8"
                    id="dispatch-header"
                >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                        <div className="flex items-center gap-3 sm:gap-4">
                            <BsBroadcast className="w-10 h-10 sm:w-12 sm:h-12 text-premium-gold flex-shrink-0" />
                            <div>
                                <h1 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-transparent bg-clip-text bg-gradient-to-r from-premium-gold-light to-premium-gold leading-tight">
                                    {t('dispatch.title')}
                                </h1>
                                <p className="text-white/60 text-sm sm:text-base mt-2">
                                    {t('dispatch.subtitle')}
                                </p>
                            </div>
                        </div>
                        <button
                            id="dispatch-create-task"
                            onClick={() => { setShowPostForm(true); window.history.pushState({ dispatchLevel: 'post' }, '', window.location.hash); }}
                            className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-premium-gold-dark to-premium-gold-light text-premium-wood-dark font-bold rounded-xl hover:shadow-[0_0_20px_rgba(212,175,55,0.5)] transition-all transform hover:scale-105 active:scale-95 flex items-center justify-center gap-2 text-base shadow-lg"
                        >
                            <BsPlusCircle className="w-5 h-5" />
                            {t('dispatch.post_job')}
                        </button>
                    </div>

                    {/* Dual-Core Mode Toggle */}
                    <div id="dispatch-dual-mode" className="flex p-1 bg-white/5 rounded-2xl mb-8 max-w-md mx-auto sm:mx-0 border border-white/5 backdrop-blur-md">
                        <button
                            onClick={() => setActiveTab('open-tasks')}
                            className={`flex - 1 py - 3 px - 4 rounded - xl text - sm font - bold transition - all flex items - center justify - center gap - 2 ${activeTab === 'open-tasks' || activeTab === 'matches' ? 'bg-premium-gold text-premium-wood-dark shadow-lg' : 'text-white/60 hover:text-white hover:bg-white/5'} `}
                        >
                            <BsBriefcase className="w-4 h-4" />
                            {t('dispatch.professional_skill')}
                        </button>
                        <button
                            onClick={() => setActiveTab('community-tasks')} // Later this will filter to community
                            className={`flex-1 py-3 px-4 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 ${activeTab === 'community-tasks' ? 'bg-premium-gold text-premium-wood-dark shadow-lg' : 'text-white/60 hover:text-white hover:bg-white/5'}`}
                        >
                            <BsLightningCharge className="w-4 h-4" />
                            {t('dispatch.community_instant')}
                        </button>
                    </div>

                    {/* Quick Filters */}
                    <div className="flex flex-wrap gap-2 mb-6 items-center">
                        {/* Near Me Toggle */}
                        <button
                            onClick={getUserLocation}
                            className={`px - 4 py - 2 rounded - full text - xs font - bold border transition - all flex items - center gap - 2 ${useLocationFilter ? 'bg-premium-gold text-premium-wood-dark border-premium-gold' : 'bg-transparent border-white/10 text-white/50 hover:border-white/30 hover:text-white'} `}
                        >
                            <BsGeoAlt />
                            Near Me {useLocationFilter && `(${radius}km)`}
                        </button>

                        {/* Radius Slider (Only visible when Near Me is active) */}
                        <AnimatePresence>
                            {useLocationFilter && (
                                <motion.div
                                    initial={{ opacity: 0, width: 0, marginLeft: 0 }}
                                    animate={{ opacity: 1, width: 'auto', marginLeft: 8 }}
                                    exit={{ opacity: 0, width: 0, marginLeft: 0 }}
                                    className="flex items-center gap-2 bg-white/5 px-3 py-1.5 rounded-full border border-white/10 overflow-hidden"
                                >
                                    <span className="text-white/60 text-xs whitespace-nowrap">Dist:</span>
                                    <input
                                        type="range"
                                        min="1"
                                        max="100"
                                        value={radius}
                                        onChange={(e) => setRadius(parseInt(e.target.value))}
                                        className="w-24 accent-premium-gold"
                                    />
                                    <span className="text-premium-gold text-xs font-bold w-8">{radius}km</span>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        <div className="w-px h-6 bg-white/10 mx-2" />

                        {[
                            { id: 'all', label: t('dispatch.filter_all') },
                            { id: 'remote', label: t('dispatch.filter_remote') },
                            { id: 'on-site', label: t('dispatch.filter_on_site') },
                            { id: 'urgent', label: t('dispatch.filter_urgent') },
                            { id: 'no-cv', label: t('dispatch.filter_no_cv') }
                        ].map((filter) => (
                            <button
                                key={filter.id}
                                onClick={() => setQuickFilter(filter.id as typeof quickFilter)}
                                className={`px-4 py-2 rounded-full text-xs font-bold border transition-all ${quickFilter === filter.id
                                    ? 'bg-white/10 border-premium-gold text-premium-gold shadow-[0_0_20px_rgba(212,175,55,0.3)]'
                                    : 'bg-transparent border-white/10 text-white/50 hover:border-white/30 hover:text-white'
                                    }`}
                            >
                                {filter.label}
                            </button>
                        ))}
                    </div>
                </motion.div>

                {/* Tabs */}
                <div id="dispatch-filters" className="flex gap-1 sm:gap-2 mb-8 border-b border-premium-gold/20 pb-0 overflow-x-auto overflow-y-hidden scrollbar-hide no-scrollbar -mx-4 px-4 sm:mx-0 sm:px-0 scroll-smooth">
                    <div id="dispatch-tab-open" className="flex-shrink-0">
                        <TabButton
                            active={activeTab === 'open-tasks'}
                            onClick={() => setActiveTab('open-tasks')}
                            icon={<BsBroadcast />}
                            label={t('dispatch.browse_tabs.browse')}
                            count={openTasks.length}
                        />
                    </div>
                    <div id="dispatch-tab-matches" className="flex-shrink-0">
                        <TabButton
                            active={activeTab === 'matches'}
                            onClick={() => setActiveTab('matches')}
                            icon={<BsBroadcast />}
                            label={t('dispatch.browse_tabs.recommended')}
                            count={matches.length}
                        />
                    </div>
                    <div id="dispatch-tab-mine" className="flex-shrink-0">
                        <TabButton
                            active={activeTab === 'my-tasks'}
                            onClick={() => setActiveTab('my-tasks')}
                            icon={<BsClock />}
                            label={t('dispatch.browse_tabs.my_jobs')}
                            count={myTasks.length}
                        />
                    </div>
                </div>

                {/* Content */}
                <div id="dispatch-board" className="space-y-4">
                    {loading ? (
                        <div className="text-center py-12">
                            <div className="animate-spin w-12 h-12 border-4 border-premium-gold border-t-transparent rounded-full mx-auto mb-4"></div>
                            <p className="text-white/70">Loading...</p>
                        </div>
                    ) : (
                        <>
                            {(activeTab === 'open-tasks' || activeTab === 'community-tasks') && (
                                <OpenTasksTab
                                    tasks={openTasks.filter(task => {
                                        // First, filter by tab category
                                        let categoryMatch = false;
                                        if (activeTab === 'open-tasks') {
                                            // Professional & Skill Gigs
                                            categoryMatch = ['professional', 'semi-skilled'].includes(task.category || 'professional');
                                        } else {
                                            // Community & Instant
                                            categoryMatch = ['informal', 'emergency'].includes(task.category || '');
                                        }

                                        if (!categoryMatch) return false;

                                        // Then apply quick filters
                                        if (quickFilter === 'all') return true;
                                        if (quickFilter === 'remote') return task.mode === 'remote';
                                        if (quickFilter === 'on-site') return task.mode === 'on-site';
                                        if (quickFilter === 'urgent') return task.urgency === 'immediate';
                                        if (quickFilter === 'no-cv') return ['informal', 'emergency'].includes(task.category || '');

                                        return true;
                                    })}
                                    onApply={handleApplyClick}
                                    onReport={setReportingTaskId}
                                    onClick={handleTaskClick}
                                />
                            )}
                            {activeTab === 'matches' && (
                                <MatchesTab
                                    tasks={matches}
                                    onAccept={handleAccept}
                                    onDecline={handleDecline}
                                    onReport={setReportingTaskId}
                                    onClick={handleTaskClick}
                                />
                            )}
                            {activeTab === 'my-tasks' && (
                                <MyTasksTab
                                    tasks={myTasks}
                                    onDelete={handleDelete}
                                    onStatusChange={handleStatusChange}
                                    onClick={handleTaskClick}
                                    onEdit={handleEditTask}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Post Task Modal */}
            {showPostForm && (
                <TaskPostingForm
                    onClose={() => {
                        setShowPostForm(false);
                        setSelectedTask(null);
                    }}
                    onSuccess={handleTaskPosted}
                    initialData={selectedTask || undefined}
                />
            )}

            {/* Task Detail Modal */}
            <AnimatePresence>
                {(selectedTask || autoTaskId) && (
                    <TaskDetailModal
                        taskId={selectedTask?._id || autoTaskId || ''}
                        initialTask={selectedTask || undefined}
                        onClose={handleCloseModal}
                        onDelete={activeTab === 'my-tasks' ? handleDelete : undefined}
                        onStatusChange={activeTab === 'my-tasks' ? handleStatusChange : undefined}
                        initialView={modalView}
                    />
                )}
            </AnimatePresence>

            {/* Notification Toast */}
            <AnimatePresence>
                {notification && (
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className={`fixed bottom-8 left-1/2 -translate-x-1/2 z-[100] px-6 py-3 rounded-fluid-xl shadow-premium border flex items-center gap-3 backdrop-blur-xl ${notification.type === 'success' ? 'bg-green-500/20 border-green-500/50 text-green-300' : 'bg-red-500/20 border-red-500/50 text-red-300'
                            }`}
                    >
                        <span>{notification.message}</span>
                        <button onClick={() => setNotification(null)} className="text-white/50 hover:text-white">&times;</button>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Report Task Modal */}
            <AnimatePresence>
                {reportingTaskId && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setReportingTaskId(null)}
                            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
                        />
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="relative w-full max-w-md glass-effect p-8 rounded-fluid-2xl border border-red-500/30 overflow-hidden"
                        >
                            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-500 to-orange-500" />
                            <h3 className="text-2xl font-serif text-white mb-6">Report Implementation Risk</h3>

                            <div className="space-y-6">
                                <div>
                                    <label className="block text-xs uppercase font-bold text-white/40 mb-2">Reason for Reporting</label>
                                    <select
                                        value={reportReason}
                                        onChange={(e) => setReportReason(e.target.value)}
                                        className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white outline-none focus:border-red-500"
                                    >
                                        <option value="scam">Potential Scam / Fraud</option>
                                        <option value="exploitative_pay">Exploitive or Low Pay</option>
                                        <option value="unsafe_conditions">Unsafe / Illegal Conditions</option>
                                        <option value="spam">Spam or Irrelevant</option>
                                        <option value="misleading">Misleading Description</option>
                                        <option value="other">Other Violation</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-xs uppercase font-bold text-white/40 mb-2">Additional Context</label>
                                    <textarea
                                        value={reportDescription}
                                        onChange={(e) => setReportDescription(e.target.value)}
                                        placeholder="Please provide details to help our moderators..."
                                        rows={4}
                                        className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white text-sm outline-none focus:border-red-500"
                                    />
                                </div>

                                <div className="flex gap-3">
                                    <button
                                        onClick={() => setReportingTaskId(null)}
                                        className="flex-1 py-3 bg-white/10 text-white font-bold rounded-lg hover:bg-white/20"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleReportSubmit}
                                        disabled={reportLoading || !reportDescription}
                                        className="flex-1 py-3 bg-red-600 text-white font-bold rounded-lg hover:bg-red-500 disabled:opacity-50 flex items-center justify-center gap-2"
                                    >
                                        {reportLoading ? <div className="animate-spin w-4 h-4 border-2 border-white/30 border-t-white rounded-full" /> : 'Submit Report'}
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

// Tab Button Component
interface TabButtonProps {
    active: boolean;
    onClick: () => void;
    icon: React.ReactNode;
    label: string;
    count?: number;
}

const TabButton: React.FC<TabButtonProps> = ({ active, onClick, icon, label, count }) => (
    <button
        onClick={onClick}
        className={`
            px-4 sm:px-6 py-2.5 sm:py-3 rounded-t-lg font-semibold transition-all duration-300 flex items-center gap-2 whitespace-nowrap text-sm sm:text-base
      ${active
                ? 'bg-gradient-to-br from-white/10 to-transparent border-b-2 border-premium-gold text-premium-gold'
                : 'text-white/60 hover:text-white/90 hover:bg-white/5'
            }
`}
    >
        {icon}
        {label}
        {count !== undefined && count > 0 && (
            <span className="ml-1 sm:ml-2 px-1.5 sm:px-2 py-0.5 sm:py-1 bg-premium-gold/20 rounded-full text-[10px] sm:text-xs text-premium-gold">
                {count}
            </span>
        )}
    </button>
);

// Open Tasks Tab (Remote Jobs)
// OpenTasksTab (Remote Jobs)
interface OpenTasksTabProps {
    tasks: DispatchTask[];
    onApply: (id: string) => void;
    onReport: (id: string) => void;
    onClick: (task: DispatchTask) => void;
}

const OpenTasksTab: React.FC<OpenTasksTabProps> = ({ tasks, onApply, onReport, onClick }) => {
    if (tasks.length === 0) {
        return (
            <div className="text-center py-16 glass-effect rounded-fluid-xl p-8">
                <BsBroadcast className="w-16 h-16 text-premium-gold/50 mx-auto mb-4" />
                <h3 className="text-2xl font-serif text-white mb-2">No open jobs currently</h3>
                <p className="text-white/60">
                    Check back later for new remote work opportunities.
                </p>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {tasks.map((task) => (
                <TaskCard
                    key={task._id}
                    task={task}
                    variant="match" // Reusing match variant for actionable buttons
                    onApply={onApply}
                    onReport={onReport}
                    onClick={onClick}
                />
            ))}
        </div>
    );
};

// Matches Tab
interface MatchesTabProps {
    tasks: DispatchTask[];
    onAccept: (id: string) => void;
    onDecline: (id: string) => void;
    onReport: (id: string) => void;
    onClick: (task: DispatchTask) => void;
}

const MatchesTab: React.FC<MatchesTabProps> = ({ tasks, onAccept, onDecline, onReport, onClick }) => {
    if (tasks.length === 0) {
        return (
            <div className="text-center py-16 glass-effect rounded-fluid-xl p-8">
                <BsBroadcast className="w-16 h-16 text-premium-gold/50 mx-auto mb-4" />
                <h3 className="text-2xl font-serif text-white mb-2">No matches yet</h3>
                <p className="text-white/60">
                    We'll notify you when tasks match your skills specifically.
                </p>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {tasks.map((task) => (
                <TaskCard
                    key={task._id}
                    task={task}
                    variant="match"
                    onAccept={onAccept}
                    onDecline={onDecline}
                    onReport={onReport}
                    onClick={onClick}
                />
            ))}
        </div>
    );
};

// My Tasks Tab
interface MyTasksTabProps {
    tasks: DispatchTask[];
    onDelete: (id: string) => void;
    onStatusChange: (id: string, status: string) => void;
    onClick: (task: DispatchTask) => void;
    onEdit: (task: DispatchTask) => void;
}

const MyTasksTab: React.FC<MyTasksTabProps> = ({ tasks, onDelete, onStatusChange, onClick, onEdit }) => {
    if (tasks.length === 0) {
        return (
            <div className="text-center py-16 glass-effect rounded-fluid-xl p-8">
                <BsClock className="w-16 h-16 text-premium-gold/50 mx-auto mb-4" />
                <h3 className="text-2xl font-serif text-white mb-2">No jobs posted</h3>
                <p className="text-white/60">
                    Click "Post Job" to create a task dispatch.
                </p>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {tasks.map((task) => (
                <TaskCard
                    key={task._id}
                    task={task}
                    variant="posted"
                    onDelete={onDelete}
                    onStatusChange={onStatusChange}
                    onClick={onClick}
                    onEdit={onEdit}
                />
            ))}
        </div>
    );
};

export default Dispatch;
